/**
 * API Service
 *
 * Provides typed API calls to the Hono backend.
 * Uses Clerk for authentication tokens.
 */

import type {
  TripSummaryWithPermission,
  TripScheduleData,
  TripLeg,
  LibraryData,
  POI,
  Tag,
  CategoryTag,
  ChangelogEvent,
  CreateTripRequest,
  PermissionLevel,
  TripShare,
  UserProfile,
  InviteLink,
  InviteLinkInfo,
  AcceptInviteResult,
  Collection,
  CollectionWithPois,
  CollectionVisibility,
  CollectionInviteLink,
  CollectionInviteLinkInfo,
  AcceptCollectionInviteResult,
  PlacesSearchResponse,
  PlaceResult,
  ScheduledPOI,
  TripNote,
} from "@poi-planner/shared";

// API URL from environment
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8787";

function normalizeDateOnly(value: unknown): string {
  if (!value) return "";
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  const text = String(value);
  return text.includes("T") ? text.slice(0, 10) : text;
}

// Public config type (returned by /api/users/config)
export interface PublicConfig {
  mapboxApiKey: string | null;
}

type ClerkLike = {
  session?: { getToken: () => Promise<string | null> } | null;
};

type ApiErrorPayload = {
  code?: string;
  error?: string;
};

type ApiErrorLike = {
  status?: number;
  code?: string;
};

// Server wire format: schedule maps travel as top-level arrays/records and
// accommodation configs as a separate array (client keeps a poiId record)
type ServerAccommodationConfig = {
  poiId: string;
  color: number;
  checkInTime?: string | null;
  checkOutTime?: string | null;
};

type ServerTripPayload = {
  trip: TripScheduleData["trip"];
  schedule: {
    scheduledPOIs: ScheduledPOI[];
    notes: TripNote[];
    sleep: Record<string, string>;
    timezoneOverrides: Record<string, string>;
  };
  accommodationConfigs: ServerAccommodationConfig[];
  legs?: TripLeg[];
};

/**
 * A loaded trip plus its legs. Legs are granular (not snapshot data), so they
 * ride alongside TripScheduleData rather than inside it — keeping the shared
 * save contract free of anything the full-trip PUT does not write.
 */
/**
 * Leg mutations bump the trip revision server-side. The caller must fold the
 * new value into the cached trip, or the next full autosave sends a stale
 * revision and loses its unsaved schedule edits to the conflict reload.
 */
export interface LegMutationResult {
  leg: TripLeg;
  revision: number;
}

export interface PoiLegMembershipResult {
  poiId: string;
  legIds: string[];
  revision: number;
}

export interface TripLoadResult {
  trip: TripScheduleData["trip"];
  schedule: TripScheduleData["schedule"];
  legs: TripLeg[];
}

function toTripScheduleData(data: ServerTripPayload): TripScheduleData {
  const configsRecord: TripScheduleData["schedule"]["accommodationConfigs"] =
    {};
  for (const cfg of data.accommodationConfigs || []) {
    configsRecord[cfg.poiId] = {
      color: cfg.color,
      checkInTime: cfg.checkInTime ?? undefined,
      checkOutTime: cfg.checkOutTime ?? undefined,
    };
  }

  return {
    trip: {
      ...data.trip,
      startDate: normalizeDateOnly(data.trip.startDate),
      endDate: normalizeDateOnly(data.trip.endDate),
    },
    schedule: {
      scheduledPOIs: data.schedule.scheduledPOIs || [],
      notes: data.schedule.notes || [],
      accommodationConfigs: configsRecord,
      sleep: data.schedule.sleep || {},
      timezoneOverrides: data.schedule.timezoneOverrides || {},
    },
  };
}

function isApiErrorLike(error: unknown): error is ApiErrorLike {
  return typeof error === "object" && error !== null && "status" in error;
}

// Fetch public config (no auth required)
export async function getPublicConfig(): Promise<PublicConfig> {
  const response = await fetch(`${API_URL}/api/users/config`);
  if (!response.ok) {
    throw new Error("Failed to fetch public config");
  }
  return response.json();
}

// Clerk instance (set by ClerkProvider)
let clerkInstance: ClerkLike | null = null;

export function setClerkInstance(clerk: ClerkLike) {
  clerkInstance = clerk;
}

// ── Fetch Helper ──

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function isTripRevisionConflict(error: unknown): boolean {
  return (
    isApiErrorLike(error) &&
    error.status === 409 &&
    error.code === "TRIP_REVISION_CONFLICT"
  );
}

async function fetchWithAuth<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  if (!clerkInstance) {
    throw new ApiError("NOT_INITIALIZED", "Clerk not initialized", 500);
  }

  const token = await clerkInstance.session?.getToken();
  if (!token) {
    throw new ApiError("NOT_AUTHENTICATED", "User not authenticated", 401);
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const data: ApiErrorPayload = await response.json().catch(() => ({}));
    throw new ApiError(
      data.code || "API_ERROR",
      data.error || `Request failed with status ${response.status}`,
      response.status,
    );
  }

  return response.json();
}

async function fetchPublic<T>(path: string): Promise<T> {
  const response = await fetch(`${API_URL}${path}`);
  if (!response.ok) {
    const data: ApiErrorPayload = await response.json().catch(() => ({}));
    throw new ApiError(
      data.code || "API_ERROR",
      data.error || `Request failed with status ${response.status}`,
      response.status,
    );
  }
  return response.json();
}

// ── API Interface (for compatibility) ──

export interface ApiService {
  listTrips(): Promise<TripSummaryWithPermission[]>;
  createNewTrip(req: CreateTripRequest): Promise<TripScheduleData>;
  loadTripById(id: string): Promise<TripLoadResult | null>;
  saveTripById(id: string, data: TripScheduleData): Promise<number>;
  deleteTripById(id: string): Promise<void>;
  saveTripChangelog(tripId: string, events: ChangelogEvent[]): Promise<void>;
  getTripChangelog(tripId: string): Promise<ChangelogEvent[]>;
  getActivityFeed(tripId?: string): Promise<ChangelogEvent[]>;
  transitionActivity(
    activityId: string,
    transition: "undo" | "redo",
  ): Promise<ChangelogEvent>;
  loadLibrary(): Promise<LibraryData>;
  savePOIs(pois: POI[]): Promise<void>;
  saveLibraryChangelog(events: ChangelogEvent[]): Promise<void>;
  createTag(tag: Omit<Tag, "id">): Promise<Tag>;
  updateTag(id: string, updates: Partial<Omit<Tag, "id">>): Promise<Tag>;
  deleteTag(id: string): Promise<void>;
  loadTags(): Promise<Tag[]>;
  createCategoryTag(name: string): Promise<CategoryTag>;
  updateCategoryTag(id: string, name: string): Promise<CategoryTag>;
  deleteCategoryTag(id: string): Promise<void>;
  setCategoryTagMembers(id: string, tagIds: string[]): Promise<CategoryTag>;
  setTagCategories(tagId: string, categoryTagIds: string[]): Promise<void>;
  reorderCategoryTags(categoryTagIds: string[]): Promise<void>;
  importLibrary(data: NonNullable<LibraryData>): Promise<void>;
  getTripPermission(tripId: string): Promise<PermissionLevel | "owner" | null>;
  shareTrip(
    tripId: string,
    email: string,
    permission: PermissionLevel,
  ): Promise<TripShare>;
  updateSharePermission(
    shareId: string,
    permission: PermissionLevel,
  ): Promise<void>;
  removeShare(shareId: string): Promise<void>;
  getTripShares(tripId: string): Promise<TripShare[]>;
  getUserProfile(userId: string): Promise<UserProfile | null>;
  searchUsersByEmail(prefix: string): Promise<UserProfile[]>;
  createInviteLink(
    tripId: string,
    permission: PermissionLevel,
    label?: string,
  ): Promise<InviteLink>;
  getInviteLinks(tripId: string): Promise<InviteLink[]>;
  cancelInviteLink(linkId: string): Promise<void>;
  getInviteLinkInfo(token: string): Promise<InviteLinkInfo>;
  acceptInviteLink(token: string): Promise<AcceptInviteResult>;
  listCollections(): Promise<CollectionWithPois[]>;
  createCollection(
    name: string,
    description?: string,
    visibility?: CollectionVisibility,
  ): Promise<Collection>;
  updateCollection(
    id: string,
    updates: {
      name?: string;
      description?: string;
      visibility?: CollectionVisibility;
    },
  ): Promise<Collection>;
  deleteCollection(id: string): Promise<void>;
  getCollectionPois(collectionId: string): Promise<string[]>;
  addPoisToCollection(collectionId: string, poiIds: string[]): Promise<void>;
  removePoisFromCollection(
    collectionId: string,
    poiIds: string[],
  ): Promise<void>;
  createCollectionInviteLink(
    collectionId: string,
    permission: PermissionLevel,
    label?: string,
  ): Promise<CollectionInviteLink>;
  getCollectionInviteLinks(
    collectionId: string,
  ): Promise<CollectionInviteLink[]>;
  cancelCollectionInviteLink(linkId: string): Promise<void>;
  getCollectionInviteLinkInfo(token: string): Promise<CollectionInviteLinkInfo>;
  acceptCollectionInviteLink(
    token: string,
  ): Promise<AcceptCollectionInviteResult>;
  getTripPoiIds(tripId: string): Promise<string[]>;
  getPinnedPoiIds(tripId: string): Promise<string[]>;
  pinPoisToTrip(tripId: string, poiIds: string[]): Promise<number>;
  unpinPoisFromTrip(tripId: string, poiIds: string[]): Promise<number>;
  createTripLeg(
    tripId: string,
    input: {
      name: string;
      startDate: string;
      endDate: string;
      color?: number;
    },
  ): Promise<LegMutationResult>;
  updateTripLeg(
    tripId: string,
    legId: string,
    updates: {
      name?: string;
      startDate?: string;
      endDate?: string;
      color?: number;
    },
  ): Promise<LegMutationResult>;
  deleteTripLeg(tripId: string, legId: string): Promise<number>;
  setPoiLegMembership(
    tripId: string,
    poiId: string,
    legIds: string[],
  ): Promise<PoiLegMembershipResult>;
  addPoisToLeg(
    tripId: string,
    legId: string,
    poiIds: string[],
  ): Promise<LegMutationResult>;
  removePoiFromLeg(
    tripId: string,
    legId: string,
    poiId: string,
  ): Promise<LegMutationResult>;
  searchPlaces(query: string): Promise<PlacesSearchResponse>;
  resolvePlaceUrl(
    url: string,
  ): Promise<{ result: PlaceResult | null; error?: string }>;
}

// ── API Implementation ──

export const api: ApiService = {
  // Multi-trip endpoints
  async listTrips() {
    const data = await fetchWithAuth<{ trips: TripSummaryWithPermission[] }>(
      "/api/trips",
    );
    return data.trips.map((trip) => ({
      ...trip,
      startDate: normalizeDateOnly(trip.startDate),
      endDate: normalizeDateOnly(trip.endDate),
    }));
  },

  async createNewTrip(req) {
    const data = await fetchWithAuth<ServerTripPayload>("/api/trips", {
      method: "POST",
      body: JSON.stringify(req),
    });
    return toTripScheduleData(data);
  },

  async loadTripById(id) {
    try {
      const data = await fetchWithAuth<ServerTripPayload>(`/api/trips/${id}`);
      return { ...toTripScheduleData(data), legs: data.legs ?? [] };
    } catch (err: unknown) {
      if (isApiErrorLike(err) && err.status === 404) return null;
      throw err;
    }
  },

  async saveTripById(id, data) {
    // Convert accommodationConfigs Record to array for server
    const configsArray = Object.entries(
      data.schedule.accommodationConfigs || {},
    ).map(([poiId, cfg]) => ({
      poiId,
      color: cfg.color,
      checkInTime: cfg.checkInTime,
      checkOutTime: cfg.checkOutTime,
    }));

    const result = await fetchWithAuth<{ revision: number }>(
      `/api/trips/${id}`,
      {
        method: "PUT",
        body: JSON.stringify({
          trip: {
            revision: data.trip.revision,
            startDate: data.trip.startDate,
            endDate: data.trip.endDate,
            defaultTimezone: data.trip.defaultTimezone,
            timezoneIds: data.trip.timezoneIds,
          },
          schedule: {
            scheduledPOIs: data.schedule.scheduledPOIs,
            notes: data.schedule.notes,
            sleep: data.schedule.sleep,
            timezoneOverrides: data.schedule.timezoneOverrides,
          },
          accommodationConfigs: configsArray,
        }),
      },
    );
    return result.revision;
  },

  async deleteTripById(id) {
    await fetchWithAuth(`/api/trips/${id}`, { method: "DELETE" });
  },

  async saveTripChangelog(tripId, events) {
    await fetchWithAuth(`/api/trips/${tripId}/changelog`, {
      method: "POST",
      body: JSON.stringify(events),
    });
  },

  async getTripChangelog(tripId) {
    const data = await fetchWithAuth<{ events: ChangelogEvent[] }>(
      `/api/trips/${tripId}/changelog`,
    );
    return data.events;
  },

  async getActivityFeed(tripId) {
    const query = tripId ? `?tripId=${encodeURIComponent(tripId)}` : "";
    const data = await fetchWithAuth<{ events: ChangelogEvent[] }>(
      `/api/activity${query}`,
    );
    return data.events;
  },

  async transitionActivity(activityId, transition) {
    const data = await fetchWithAuth<{ event: ChangelogEvent }>(
      `/api/activity/${encodeURIComponent(activityId)}/${transition}`,
      { method: "POST" },
    );
    return data.event;
  },

  // Library endpoints
  async loadLibrary() {
    return fetchWithAuth<LibraryData>("/api/library");
  },

  async savePOIs(pois) {
    await fetchWithAuth("/api/library/pois", {
      method: "PUT",
      body: JSON.stringify({ pois }),
    });
  },

  async saveLibraryChangelog(events) {
    await fetchWithAuth("/api/library/changelog", {
      method: "POST",
      body: JSON.stringify(events),
    });
  },

  // Tag endpoints
  async createTag(tag) {
    return fetchWithAuth<Tag>("/api/library/tags", {
      method: "POST",
      body: JSON.stringify(tag),
    });
  },

  async updateTag(id, updates) {
    return fetchWithAuth<Tag>(`/api/library/tags/${id}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  },

  async deleteTag(id) {
    await fetchWithAuth(`/api/library/tags/${id}`, { method: "DELETE" });
  },

  async loadTags() {
    const data = await fetchWithAuth<{ tags: Tag[] }>("/api/library/tags");
    return data.tags;
  },

  async createCategoryTag(name) {
    return fetchWithAuth<CategoryTag>("/api/library/category-tags", {
      method: "POST",
      body: JSON.stringify({ name }),
    });
  },

  async updateCategoryTag(id, name) {
    return fetchWithAuth<CategoryTag>(`/api/library/category-tags/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    });
  },

  async deleteCategoryTag(id) {
    await fetchWithAuth(`/api/library/category-tags/${id}`, {
      method: "DELETE",
    });
  },

  async setCategoryTagMembers(id, tagIds) {
    return fetchWithAuth<CategoryTag>(
      `/api/library/category-tags/${id}/members`,
      { method: "PUT", body: JSON.stringify({ tagIds }) },
    );
  },

  async setTagCategories(tagId, categoryTagIds) {
    await fetchWithAuth(`/api/library/category-tags/by-tag/${tagId}`, {
      method: "PUT",
      body: JSON.stringify({ categoryTagIds }),
    });
  },

  async reorderCategoryTags(categoryTagIds) {
    await fetchWithAuth("/api/library/category-tags/order", {
      method: "PUT",
      body: JSON.stringify({ categoryTagIds }),
    });
  },

  async importLibrary(data) {
    await fetchWithAuth("/api/library/import", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  // Collaboration endpoints
  async getTripPermission(tripId) {
    try {
      const data = await fetchWithAuth<{
        permission: PermissionLevel | "owner" | null;
      }>(`/api/trips/${tripId}/permission`);
      return data.permission;
    } catch {
      return null;
    }
  },

  async shareTrip(tripId, email, permission) {
    return fetchWithAuth<TripShare>(`/api/trips/${tripId}/shares`, {
      method: "POST",
      body: JSON.stringify({ email, permission }),
    });
  },

  async updateSharePermission(shareId, permission) {
    await fetchWithAuth(`/api/trips/shares/${shareId}`, {
      method: "PUT",
      body: JSON.stringify({ permission }),
    });
  },

  async removeShare(shareId) {
    await fetchWithAuth(`/api/trips/shares/${shareId}`, { method: "DELETE" });
  },

  async getTripShares(tripId) {
    const data = await fetchWithAuth<{ shares: TripShare[] }>(
      `/api/trips/${tripId}/shares`,
    );
    return data.shares;
  },

  async getUserProfile(userId) {
    try {
      return await fetchWithAuth<UserProfile>(`/api/users/${userId}`);
    } catch {
      return null;
    }
  },

  async searchUsersByEmail(prefix) {
    const data = await fetchWithAuth<{ users: UserProfile[] }>(
      `/api/users/search?q=${encodeURIComponent(prefix)}`,
    );
    return data.users;
  },

  // Invite link endpoints
  async createInviteLink(tripId, permission, label) {
    return fetchWithAuth<InviteLink>(`/api/trips/${tripId}/invites`, {
      method: "POST",
      body: JSON.stringify({ permission, label }),
    });
  },

  async getInviteLinks(tripId) {
    const data = await fetchWithAuth<{ invites: InviteLink[] }>(
      `/api/trips/${tripId}/invites`,
    );
    return data.invites;
  },

  async cancelInviteLink(linkId) {
    await fetchWithAuth(`/api/invites/${linkId}`, { method: "DELETE" });
  },

  async getInviteLinkInfo(token) {
    return fetchPublic<InviteLinkInfo>(`/api/invites/${token}/info`);
  },

  async acceptInviteLink(token) {
    return fetchWithAuth<AcceptInviteResult>(`/api/invites/${token}/accept`, {
      method: "POST",
    });
  },

  // Collection endpoints
  async listCollections() {
    const data = await fetchWithAuth<{ collections: CollectionWithPois[] }>(
      "/api/collections",
    );
    return data.collections;
  },

  async createCollection(name, description, visibility) {
    return fetchWithAuth<Collection>("/api/collections", {
      method: "POST",
      body: JSON.stringify({ name, description, visibility }),
    });
  },

  async updateCollection(id, updates) {
    return fetchWithAuth<Collection>(`/api/collections/${id}`, {
      method: "PUT",
      body: JSON.stringify(updates),
    });
  },

  async deleteCollection(id) {
    await fetchWithAuth(`/api/collections/${id}`, { method: "DELETE" });
  },

  async getCollectionPois(collectionId) {
    const data = await fetchWithAuth<{ poiIds: string[] }>(
      `/api/collections/${collectionId}/pois`,
    );
    return data.poiIds;
  },

  async addPoisToCollection(collectionId, poiIds) {
    await fetchWithAuth(`/api/collections/${collectionId}/pois`, {
      method: "POST",
      body: JSON.stringify({ poiIds }),
    });
  },

  async removePoisFromCollection(collectionId, poiIds) {
    await fetchWithAuth(`/api/collections/${collectionId}/pois`, {
      method: "DELETE",
      body: JSON.stringify({ poiIds }),
    });
  },

  async createCollectionInviteLink(collectionId, permission, label) {
    return fetchWithAuth<CollectionInviteLink>(
      `/api/collections/${collectionId}/invites`,
      {
        method: "POST",
        body: JSON.stringify({ permission, label }),
      },
    );
  },

  async getCollectionInviteLinks(collectionId) {
    const data = await fetchWithAuth<{ invites: CollectionInviteLink[] }>(
      `/api/collections/${collectionId}/invites`,
    );
    return data.invites;
  },

  async cancelCollectionInviteLink(linkId) {
    await fetchWithAuth(`/api/collection-invites/${linkId}`, {
      method: "DELETE",
    });
  },

  async getCollectionInviteLinkInfo(token) {
    return fetchPublic<CollectionInviteLinkInfo>(
      `/api/collection-invites/${token}/info`,
    );
  },

  async acceptCollectionInviteLink(token) {
    return fetchWithAuth<AcceptCollectionInviteResult>(
      `/api/collection-invites/${token}/accept`,
      { method: "POST" },
    );
  },

  // Trip POI IDs
  async getTripPoiIds(tripId) {
    const data = await fetchWithAuth<{ poiIds: string[] }>(
      `/api/trips/${tripId}/poi-ids`,
    );
    return data.poiIds;
  },

  // Pinned POI endpoints
  async getPinnedPoiIds(tripId) {
    const data = await fetchWithAuth<{ poiIds: string[] }>(
      `/api/trips/${tripId}/pinned-pois`,
    );
    return data.poiIds;
  },

  async pinPoisToTrip(tripId, poiIds) {
    const result = await fetchWithAuth<{ revision: number }>(
      `/api/trips/${tripId}/pinned-pois`,
      {
        method: "POST",
        body: JSON.stringify({ poiIds }),
      },
    );
    return result.revision;
  },

  async unpinPoisFromTrip(tripId, poiIds) {
    const result = await fetchWithAuth<{ revision: number }>(
      `/api/trips/${tripId}/pinned-pois`,
      {
        method: "DELETE",
        body: JSON.stringify({ poiIds }),
      },
    );
    return result.revision;
  },

  // Trip legs (granular — deliberately outside the full-trip snapshot).
  // There is no list call: legs ride the trip GET, and a second fetch of the
  // same data could disagree with it.
  async createTripLeg(tripId, input) {
    return fetchWithAuth<LegMutationResult>(`/api/trips/${tripId}/legs`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  },

  async updateTripLeg(tripId, legId, updates) {
    return fetchWithAuth<LegMutationResult>(
      `/api/trips/${tripId}/legs/${legId}`,
      { method: "PATCH", body: JSON.stringify(updates) },
    );
  },

  async deleteTripLeg(tripId, legId) {
    const data = await fetchWithAuth<{ success: boolean; revision: number }>(
      `/api/trips/${tripId}/legs/${legId}`,
      { method: "DELETE" },
    );
    return data.revision;
  },

  async setPoiLegMembership(tripId, poiId, legIds) {
    return fetchWithAuth<PoiLegMembershipResult>(
      `/api/trips/${tripId}/pinned-pois/${poiId}/legs`,
      { method: "PUT", body: JSON.stringify({ legIds }) },
    );
  },

  async addPoisToLeg(tripId, legId, poiIds) {
    return fetchWithAuth<LegMutationResult>(
      `/api/trips/${tripId}/legs/${legId}/pois`,
      { method: "POST", body: JSON.stringify({ poiIds }) },
    );
  },

  async removePoiFromLeg(tripId, legId, poiId) {
    return fetchWithAuth<LegMutationResult>(
      `/api/trips/${tripId}/legs/${legId}/pois/${poiId}`,
      { method: "DELETE" },
    );
  },

  // Places search (proxied through server)
  async searchPlaces(query) {
    return fetchWithAuth<PlacesSearchResponse>("/api/places/search", {
      method: "POST",
      body: JSON.stringify({ query }),
    });
  },

  async resolvePlaceUrl(url) {
    return fetchWithAuth<{ result: PlaceResult | null; error?: string }>(
      "/api/places/resolve-url",
      {
        method: "POST",
        body: JSON.stringify({ url }),
      },
    );
  },
};
